import isEqual from 'deep-equal';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useConfig } from 'shared/config/ConfigContext';
import { ResultPageLayout } from 'shared/page/result/ResultPageLayout';
import { Sorting } from 'shared/page/result/Sorting';
import type { DeviceType } from 'shared/types';
import { setInitialBannerFilterValues } from 'shared/util/bannerParams';
import { queryParamToString } from 'shared/util/query';
import type { FhhState, Filters, ResultData } from '@/domain';
import * as resultActions from '@/ducks/results/resultsActions';
import { useExperiment } from '@/hooks/experiments';
import { useSearchResultsWithPricesRegular } from '@/hooks/useSearchResultsWithPricesRegular';
import { trackResultPage } from '@/lib/tracking/tracking';
import { SaveSearch } from '@/scenes/resultPage/components/saveSearch/SaveSearch';
import { parseUrl } from '@/scenes/resultPage/helpers';
import { FhhSearch } from './components/newSearch/FhhSearch';
import { SearchDetails } from './components/newSearch/SearchDetails';
import { ResultFilter } from './components/resultFilter/ResultFilter';
import { ResultList } from './components/resultList/ResultList';

export interface ResultPageProps {
    deviceType: DeviceType;
    loginId: number;
    searchId: string | null;
}

const sortingOptions = [
    { value: 'best', label: 'Mest relevant' },
    { value: 'published', label: 'Publisert' },
    { value: 'price_asc', label: 'Pris lav-høy' },
    { value: 'price_desc', label: 'Pris høy-lav' },
    { value: 'bedroom_asc', label: 'Soverom lav-høy' },
    { value: 'bedroom_desc', label: 'Soverom høy-lav' },
];

const hasSearchParams = (filters: Filters) =>
    Boolean(
        (filters.city && filters.city?.length > 0) ||
            (filters.country && filters.country?.length > 0) ||
            (filters.booking_from && filters.booking_from?.length > 0 && filters.booking_to && filters.booking_to?.length > 0) ||
            (filters.no_of_bedrooms_from && filters.no_of_bedrooms_from > 0) ||
            (filters.no_of_beds_from && filters.no_of_beds_from > 0),
    );

export const ResultPage = ({ deviceType, loginId, searchId }: ResultPageProps) =>
    searchId ? (
        <WithExactPrices deviceType={deviceType} loginId={loginId} searchId={searchId} />
    ) : (
        <WithBasePrices deviceType={deviceType} loginId={loginId} />
    );

const WithExactPrices = ({ deviceType, loginId, searchId }: ResultPageProps) => {
    const experiment = useExperiment();
    const useNewDefaultSorting = experiment?.['tjvr-breach-detection-in-boost'] === 'treatment';
    const { filters } = useSelector((state: FhhState) => state.results);
    const effectiveFilters = { ...filters, useNewDefaultSorting: useNewDefaultSorting };

    // @ts-ignore searchId can be null... please fix.
    const { data, loading } = useSearchResultsWithPricesRegular(searchId, effectiveFilters);
    return (
        <ResultPageView
            deviceType={deviceType}
            loginId={loginId}
            data={data}
            loading={loading}
            useNewDefaultSorting={useNewDefaultSorting}
        />
    );
};

const WithBasePrices = ({ deviceType, loginId }: Omit<ResultPageProps, 'searchId'>) => {
    const experiment = useExperiment();
    const useNewDefaultSorting = experiment?.['tjvr-breach-detection-in-boost'] === 'treatment';
    const { apiExternalUrl } = useConfig();

    const router = useRouter();
    const dispatch = useDispatch();

    const { data, filters } = useSelector((state: FhhState) => state.results);

    useEffect(() => {
        populateResultsIfEmpty();
    }, []);

    const populateResultsIfEmpty = () => {
        const query = router?.query || {};

        if (data.resultItems === null) {
            dispatch(
                resultActions.populateResults(
                    apiExternalUrl,
                    Object.assign({}, filters, query, { useNewDefaultSorting: useNewDefaultSorting }),
                ),
            ); // this is the trigger for the default search
        }
    };

    return (
        <ResultPageView deviceType={deviceType} loginId={loginId} data={data} loading={false} useNewDefaultSorting={useNewDefaultSorting} />
    );
};

interface ResultPageViewProps {
    deviceType: DeviceType;
    loginId: number;
    data: ResultData;
    loading: boolean;
    useNewDefaultSorting: boolean;
}

const ResultPageView = ({ deviceType, loginId, data, loading, useNewDefaultSorting }: ResultPageViewProps) => {
    const { apiExternalUrl } = useConfig();
    const dispatch = useDispatch();
    const router = useRouter();
    const searchParams = parseUrl(router.query);

    const { filters, filterData, autocompleteData } = useSelector((state: FhhState) => state.results);

    const [openSearchForm, setOpenSearchForm] = useState(false);
    const showSearchForm = openSearchForm || !hasSearchParams(filters);

    useEffect(() => {
        if (!isFalsyOrEmpty(router?.query)) {
            dispatch(resultActions.updateFilters({ ...router.query, nrFUSAds: deviceType.isProbablyMobile ? 1 : 2 }));
        }

        setInitialBannerFilterValues({
            pageType: 'result',
            page: filters.page ?? undefined,
            destination: {
                city: autocompleteData?.trackingInfo?.city || filters.city || queryParamToString(router?.query?.city),
                country: autocompleteData?.trackingInfo?.country || filters.country || queryParamToString(router?.query?.country),
            },
        });
    }, []);

    const prevFilters = useRef<Filters>(filters);
    const prevData = useRef<ResultData>();
    useEffect(() => {
        const unchangedFilters = isEqual(filters, prevFilters.current);

        if (unchangedFilters && isEqual(data, prevData.current)) {
            return;
        }
        prevFilters.current = filters;
        prevData.current = data;
        // On a page load, the stats object is empty. This object is needed for tracking, so must wait until
        // next update when stats is set before tracking. After that, track on every filter change.
        if (data.stats?.numResults !== undefined) {
            trackResultPage('Grid', { data, filters });
        }

        if (unchangedFilters) {
            return;
        }
        router?.replace({ pathname: router.pathname, query: filters }, undefined, { shallow: true });
    }, [filters, data.stats]);

    const isFalsyOrEmpty = (value: unknown) =>
        !value || (Object.prototype.toString.call(value) === '[object Object]' && JSON.stringify(value) === '{}');

    const filterResults = (filter: Filters) =>
        dispatch(resultActions.filterResults(apiExternalUrl, { ...filter, useNewDefaultSorting: useNewDefaultSorting }));

    const searchPanel = showSearchForm ? (
        <FhhSearch filters={filters} openSearchForm={() => setOpenSearchForm(false)} />
    ) : (
        <SearchDetails filters={filters} openSearchForm={() => setOpenSearchForm(true)} />
    );

    return (
        <ResultPageLayout
            title="Feriehjem og hytter"
            breadcrumbs={{ path: 'feriehus-hytteutleie' }}
            search={searchPanel}
            filters={<ResultFilter filters={filters} filterData={filterData} onFilterChanged={filterResults} />}
            sortingSection="feriehus"
            sorting={
                <Sorting
                    sortOptions={sortingOptions}
                    defaultSort="best"
                    selectedSort={filters.sorting ?? undefined}
                    onChange={(value) => filterResults({ sorting: value })}
                />
            }
            saveSearch={
                <SaveSearch
                    filters={filters}
                    autocompleteData={autocompleteData}
                    isLoggedIn={loginId != null}
                    data-testid="save-search-box"
                />
            }
            mapUrl={`${router.basePath}/kart/?${searchParams.toString()}`}>
            <ResultList
                results={data.resultItems ?? false}
                stats={data.stats}
                filters={filters}
                onFilterChanged={filterResults}
                numberOfPreloadedImages={6}
                loginId={loginId}
                loading={loading}
            />
        </ResultPageLayout>
    );
};
